import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/constants/testIds";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Brand from "@/components/Brand";

const HERO = "https://images.pexels.com/photos/27555583/pexels-photo-27555583.jpeg";
const DRAMATIC = "https://images.unsplash.com/photo-1727341557146-4abab94d0812";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  // DPDPA §6 requires an explicit, affirmative act of consent before we can
  // process personal data. A pre-ticked box does NOT count. Users must
  // check this themselves before the Google button becomes enabled.
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    // Post-auth destination is always /home.
    if (!loading && user) {
      navigate("/home", { replace: true });
    }
  }, [user, loading, navigate]);

  const onAuthed = (u) => {
    setUser(u);
    navigate("/home", { replace: true });
  };

  const handleGoogle = async () => {
    // DPDPA §6 — affirmative consent required before we can process any
    // personal data (Google Sign-In returns name + email + profile pic).
    if (!consent) {
      toast.error("Please agree to the Privacy Policy & Terms first.");
      return;
    }
    // Inside the native Capacitor shell, run the Google Sign-In SDK and
    // exchange the resulting idToken with FastAPI for a Stylebay session.
    try {
      const { isNative, nativeGoogleSignIn } = await import("@/lib/native");
      if (isNative()) {
        const res = await nativeGoogleSignIn();
        if (!res?.idToken) { toast.error("Google sign-in canceled"); return; }
        const { data } = await api.post("/auth/google/verify", { id_token: res.idToken });
        onAuthed(data.user);
        return;
      }
    } catch (e) {
      // Native Google SDK errors come through as { code, message } — surface
      // both so we can diagnose (e.g. code "10" = DEVELOPER_ERROR meaning the
      // installed APK's SHA-1 isn't registered on the Google Cloud OAuth
      // Android client).
      const code = e?.code ? ` (code ${e.code})` : "";
      const detail = e?.response?.data?.detail || e?.message || e?.error || "unknown error";
      toast.error(`Google sign-in failed${code}: ${detail}`);
      // eslint-disable-next-line no-console -- surfaced intentionally
      console.error("[GoogleAuth]", e);
      return;
    }
    // Web fallback — Emergent-managed OAuth redirect.
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-[100dvh] flex flex-col lg:grid lg:grid-cols-12 bg-background text-foreground"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/*
        HERO — on mobile the featured image was being cropped by the Android
        status bar AND leaving a huge black gap before the Google button.
        Fix: mobile hero is now aspect-based (16:14) with `object-position:
        center 25%` so the model's face isn't chopped off, and both columns
        flow tightly (no forced 320px block + separate form padding).
      */}
      <div className="lg:col-span-7 relative overflow-hidden border-b lg:border-b-0 lg:border-r border-border shrink-0 aspect-[16/14] sm:aspect-[16/10] lg:aspect-auto lg:h-auto">
        <img
          src={HERO}
          alt="Editorial fashion"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center 25%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-6 lg:p-14 text-white">
          <div className="flex items-baseline gap-2">
            <Brand size="md" className="text-white" />
            <span className="text-[10px] uppercase tracking-[0.3em] opacity-80">Atelier</span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <p className="text-[10px] lg:text-xs uppercase tracking-[0.3em] opacity-80">Volume 1 · Spring</p>
            <h1 className="font-serif text-3xl lg:text-6xl tracking-tight leading-[1.05] mt-2 lg:mt-4">
              A wardrobe
              <br />
              <em className="italic font-medium opacity-90">that knows you.</em>
            </h1>
            <p className="hidden lg:block mt-6 text-base lg:text-lg max-w-md opacity-90">
              Curated styling, instant try-ons and a closet that anticipates your week —
              all crafted by a private AI couturier.
            </p>
          </motion.div>
          <div className="hidden lg:grid grid-cols-3 max-w-md text-[11px] uppercase tracking-[0.2em] opacity-90">
            <div><p className="font-serif text-3xl tracking-tight normal-case">240k</p><p className="mt-1">Looks Styled</p></div>
            <div><p className="font-serif text-3xl tracking-tight normal-case">96%</p><p className="mt-1">Confidence Lift</p></div>
            <div><p className="font-serif text-3xl tracking-tight normal-case">12</p><p className="mt-1">Cities Wearing</p></div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 flex-1 flex items-center justify-center px-6 py-8 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">
          <p className="eyebrow">Sign in · Bespoke styling</p>
          <h2 className="editorial-h2 mt-3">Step into your atelier.</h2>

          <div className="mt-8 space-y-3">
            <label
              className="flex items-start gap-3 cursor-pointer select-none"
              data-testid="login-consent-label"
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="login-consent-checkbox"
                className="mt-1 h-4 w-4 accent-accent flex-shrink-0"
              />
              <span className="text-xs leading-relaxed text-muted-foreground">
                I have read and agree to the{" "}
                <Link to="/privacy" className="text-foreground underline underline-offset-4">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/terms" className="text-foreground underline underline-offset-4">Terms &amp; Conditions</Link>.
                I consent to StyleBay processing my Google profile info to sign me in.
              </span>
            </label>
            <Button
              data-testid={TEST_IDS.login.googleBtn}
              onClick={handleGoogle}
              disabled={!consent}
              className="w-full h-12 rounded-none bg-accent text-accent-foreground hover:bg-accent/90 uppercase tracking-[0.22em] text-xs disabled:opacity-45"
            >
              <GoogleMark className="mr-3" /> Continue with Google
            </Button>
            <p className="text-[10px] text-muted-foreground text-center mt-3 uppercase tracking-[0.18em]">
              One tap · your Google account · no passwords.
            </p>
          </div>

          <p className="text-[10px] text-muted-foreground mt-8 uppercase tracking-[0.18em] text-center">
            Need help? See our{" "}
            <Link to="/grievance" className="underline underline-offset-4" data-testid="login-grievance-link">grievance officer</Link>.
          </p>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate("/feedback")}
              data-testid="login-feedback-btn"
              className="text-[10px] uppercase tracking-[0.18em] underline underline-offset-4 text-muted-foreground hover:text-accent transition-colors"
            >
              Send feedback
            </button>
          </div>

          <div className="relative mt-8 border border-border overflow-hidden hidden lg:block">
            <img src={DRAMATIC} alt="" className="h-32 w-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-background/85 backdrop-blur">
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">From the lookbook</p>
              <p className="font-serif text-base mt-1">&ldquo;Editorial-grade outfits, in seconds.&rdquo;</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleMark({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M12 11v3.2h4.5c-.2 1.2-1.3 3.6-4.5 3.6-2.7 0-4.9-2.3-4.9-5s2.2-5 4.9-5c1.6 0 2.6.7 3.2 1.2l2.2-2.1C16 5.5 14.2 4.7 12 4.7 7.9 4.7 4.6 8 4.6 12s3.3 7.3 7.4 7.3c4.3 0 7.1-3 7.1-7.2 0-.5 0-.8-.1-1.2L12 11z"/>
      <path fill="#FBBC05" d="M5.5 14.1l-2.6 2C4 18.5 7.3 19.3 9.7 19.3l.4-2.7c-1.7-.1-3.6-1-4.6-2.5z"/>
      <path fill="#34A853" d="M19 12.1c0-.5 0-.8-.1-1.2H12V14.2h4.5c-.2 1.2-1.3 3.6-4.5 3.6.3 0 4.7 0 7-3.5.1-.7.1-1.4 0-2.2z"/>
      <path fill="#4285F4" d="M5.5 9.9l-2.6-2C4 5.5 7.3 4.7 9.7 4.7c2.2 0 4 .8 4.4 1.2L11.9 8c-.6-.5-1.6-1.2-3.2-1.2-1.7 0-3.6 1-4.6 2.5z"/>
    </svg>
  );
}
